import { uniq } from 'lodash';
import {
  Avatar,
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  Typography,
  styled,
} from '@mui/material';
import { Column, Row } from 'components/Layout';
import { AdminUserRealm } from 'further-types/admin-user';
import { superOrgLogo } from 'adminConstants/files';
import useSwitchOrganisation from 'hooks/auth/useSwitchOrganisation';
import { ArrowForward } from '@mui/icons-material';
import VehicleSwitcher from './VehicleSwitcher';
import Chip from 'components/Chip';

const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.md),
  minWidth: '310px',
}));

const Logo = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(8),
  height: theme.spacing(8),
}));

const OrganisationList = styled(List)(({ theme }) => ({
  gap: theme.spacing(theme.spacingSizes.sm),
}));

const Organisation = styled(ListItemButton)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(
    theme.spacingSizes.xs,
    theme.spacingSizes.sm,
    theme.spacingSizes.xs,
    theme.spacingSizes.xs,
  ),
  border: `1px solid ${theme.palette.grey[300]}`,
  width: '310px',
  maxWidth: '100%',

  '&:hover': {
    backgroundColor: 'transparent',
    borderColor: theme.palette.grey[400],
  },
}));

type Props = {
  currentRealm?: AdminUserRealm;
  onVehicleChange: () => void;
  currentOrganisation: {
    firmName: string;
    investmentFirmLogo: string;
  };

  otherOrganisations: Array<{
    auth0OrgId?: string;
    firmName?: string;
    firmLogo?: string;
    realm?: AdminUserRealm;
  }>;
};

const OrganisationSwitcherModal: React.FC<Props> = ({
  currentOrganisation,
  otherOrganisations,
  currentRealm,
  onVehicleChange,
}) => {
  const onSwitchOrganisation = useSwitchOrganisation();
  const noOtherOrgs = otherOrganisations.length === 0;

  const uniqRealms = uniq([
    ...otherOrganisations.map((org) => org.realm),
    currentRealm,
  ]);
  const showRealmPill = uniqRealms.length > 1;

  return (
    <Container>
      <Column spacing="sm">
        <Row centered spacing="xs">
          <Logo
            src={
              currentRealm === AdminUserRealm.Super
                ? superOrgLogo
                : currentOrganisation.investmentFirmLogo
            }
            variant="rounded"
          />
          <Typography variant="h6">{currentOrganisation.firmName}</Typography>
        </Row>
        <VehicleSwitcher
          onVehicleChange={onVehicleChange}
          useExpandedState={noOtherOrgs}
        />
        {!noOtherOrgs ? (
          <>
            <Divider />
            <OrganisationList disablePadding>
              {otherOrganisations
                .slice()
                .sort((a, b) =>
                  (a.firmName ?? '').localeCompare(b.firmName ?? ''),
                )
                .map((organisation) => (
                  <ListItem key={organisation.auth0OrgId} disablePadding>
                    <Organisation
                      disableGutters
                      onClick={() => {
                        onSwitchOrganisation(organisation);
                      }}
                    >
                      <Row spacing="xs" centered>
                        <Logo
                          src={
                            organisation.realm === AdminUserRealm.Super
                              ? superOrgLogo
                              : organisation.firmLogo
                          }
                          variant="rounded"
                        />
                        <Typography variant="h6">
                          {organisation.realm === AdminUserRealm.Super
                            ? 'Further'
                            : organisation.firmName}
                        </Typography>
                      </Row>
                      <Row spacing="xs" centered>
                        {showRealmPill ? (
                          <Chip variant="success" small>
                            {organisation.realm === AdminUserRealm.Eis
                              ? 'S/EIS'
                              : organisation.realm}
                          </Chip>
                        ) : null}
                        <ArrowForward color="action" fontSize="small" />
                      </Row>
                    </Organisation>
                  </ListItem>
                ))}
            </OrganisationList>
          </>
        ) : null}
      </Column>
    </Container>
  );
};

export default OrganisationSwitcherModal;
